import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import Container from 'reactstrap/lib/Container';
import { get, isArray } from 'lodash-es';
import { Link } from 'site-modules/shared/components/link/link';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { InventoryEntities } from 'client/data/models/inventory';
import { InventorySimilarVinsModel } from 'client/data/models/inventory-similar-vins';
import { EasListingAd } from 'site-modules/shared/components/native-ad/eas-listing-ad/eas-listing-ad';
import { SimilarInventoryCard } from 'site-modules/shared/components/inventory/similar-inventory-card/similar-inventory-card';
import { hasDealerAddressInfo } from 'client/site-modules/shared/utils/inventory-utils/has-dealer-address-info';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { withInventoryListTracking } from 'site-modules/shared/components/inventory-list-tracking/inventory-list-tracking';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { getLastWordPlural } from 'client/utils/plural';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { isPartnerDealer } from 'site-modules/shared/utils/dealer-details-utils';
import { getCards } from 'client/site-modules/shared/utils/inventory/get-cards';
import { UsurpInventoryCard } from 'client/site-modules/shared/components/inventory/usurp-inventory-card/usurp-inventory-card';
import {
  CREATIVE_ID,
  PARTNER_CREATIVE_ID,
  VEHICLES_TO_SHOW,
  VEHICLES_TO_SHOW_BUTTON,
} from 'client/site-modules/shared/constants/inventory/vdp-similar-vehicles';

import './vdp-similar-vehicles.scss';

export function VdpSimilarVehiclesContentUI({
  sectionTitle = '',
  srpLink = '',
  vehicles = [],
  hasMoreInventory = false,
  isMobile = false,
  isNewDesign = false,
  visitorLocation = {},
  radius = 25,
  paymentType = '',
  forceLease = false,
  isPartnerListing = false,
  injectEasListing = false,
  isUsedBasic = false,
}) {
  const cardProps = {
    visitorLocation,
    radius,
    isMobile,
    paymentType,
    forceLease,
    injectEasListing,
    isUsedBasic,
    isSimilarCards: isNewDesign,
    disableInventoryMLPhotoSort: true,
  };
  const addViewAllButton = !isPartnerListing && vehicles.length >= VEHICLES_TO_SHOW_BUTTON;
  const addViewAllCard = isPartnerListing && vehicles.length < VEHICLES_TO_SHOW;
  const addViewAllLink = !addViewAllCard || !isPartnerListing;
  const addEasListings = injectEasListing && isUsedBasic;

  return (
    <Row
      data-tracking-parent={isNewDesign && isPartnerListing ? PARTNER_CREATIVE_ID : CREATIVE_ID}
      className={classnames('vdp-similar-vehicles', {
        'new-design container': isNewDesign,
        'bg-blue-90 overlay-white-40 pt-2 mb-2 mt-2 pb-0_5': isNewDesign && isMobile && !isPartnerListing,
      })}
    >
      <Col xs={12} tag="h2" className={classnames('heading-3 text-start', { 'px-0 mb-1_5': isNewDesign })}>
        {sectionTitle}
        {!isNewDesign && (
          <Link to={srpLink} className="see-all-link ms-1" data-tracking-id="view_search_result">
            <span className="text-primary">See all</span>
            <i className="icon-arrow-right4 legal text-primary align-middle ms-0_25" aria-hidden />
          </Link>
        )}
        {addViewAllLink && isNewDesign && (
          <Link
            to={srpLink}
            className={classnames('see-all-link text-primary-darker text-nowrap', {
              'ms-1': !(isMobile && isPartnerListing),
              'd-block': isMobile && isPartnerListing,
            })}
            data-tracking-id="view_search_result"
          >
            <span className="fw-normal size-16">View all</span>
            <i className="icon-arrow-right4 small text-primary-darker ms-0_25" aria-hidden />
          </Link>
        )}
      </Col>
      {addEasListings && <EasListingAd mobile={isMobile} wrapperClass="mb-1 w-100 px-0" />}
      {isNewDesign ? (
        getCards({
          vehicles: [...vehicles],
          cardProps,
          srpLink,
          addViewAllCard,
          isPartnerListing,
          cardComponent: UsurpInventoryCard,
        })
      ) : (
        <Row className="row-container flex-nowrap g-0 mb-1 py-1 ps-0_25 ps-md-0 w-100">
          {vehicles.map(vehicle => (
            <Col className="column-card p-0 mb-1_5" key={vehicle.vin} sm={4} lg={3}>
              <SimilarInventoryCard
                vinData={vehicle}
                showDealerName={!hasMoreInventory}
                isOneCard={vehicles.length === 1}
                isMobile={isMobile}
                showSaveHeart={false}
                lazyLoadImage
              />
            </Col>
          ))}
        </Row>
      )}
      {addViewAllButton && isNewDesign && (
        <Button
          size="lg"
          color="outline-primary-b"
          className="mx-auto px-2 py-0_5 mb-1_5 rounded-8 w-auto"
          href={srpLink}
          data-tracking-id="view_search_result"
        >
          <span>View All</span>
        </Button>
      )}
    </Row>
  );
}

VdpSimilarVehiclesContentUI.propTypes = {
  sectionTitle: PropTypes.string,
  srpLink: PropTypes.string,
  vehicles: PropTypes.arrayOf(PropTypes.shape({})),
  hasMoreInventory: PropTypes.bool,
  isMobile: PropTypes.bool,
  isNewDesign: PropTypes.bool,
  visitorLocation: VisitorEntities.Location,
  radius: PropTypes.number,
  paymentType: PropTypes.string,
  forceLease: PropTypes.bool,
  isPartnerListing: PropTypes.bool,
  injectEasListing: PropTypes.bool,
  isUsedBasic: PropTypes.bool,
};

export const VdpSimilarVehiclesContent = withInventoryListTracking(VdpSimilarVehiclesContentUI);

/**
 * Shows section with similar vehicles.
 */
export class VdpSimilarVehiclesUI extends Component {
  static propTypes = {
    vinData: PropTypes.oneOfType([
      InventoryEntities.InventoryVin,
      PropTypes.shape({
        vehicleInfo: PropTypes.shape({
          styleInfo: PropTypes.shape({
            year: PropTypes.string,
            model: PropTypes.string,
            make: PropTypes.string,
          }),
        }),
      }),
    ]),
    inventory: PropTypes.oneOfType([InventoryEntities.Inventories, InventoryEntities.InventoriesVin]),
    isMobile: PropTypes.bool,
    isNewDesign: PropTypes.bool,
    visitorLocation: VisitorEntities.Location,
    radius: PropTypes.number,
    paymentType: PropTypes.string,
    forceLease: PropTypes.bool,
    isPartnerListing: PropTypes.bool,
    defaultSectionTitle: PropTypes.string,
    isVla: PropTypes.bool,
    isGone: PropTypes.bool,
    isFeaturedListingRetention: PropTypes.bool,
    injectEasListing: PropTypes.bool,
    isUsedBasic: PropTypes.bool,
  };

  static defaultProps = {
    vinData: {},
    inventory: [],
    isMobile: false,
    isNewDesign: false,
    visitorLocation: {},
    radius: 25,
    paymentType: '',
    forceLease: false,
    isPartnerListing: false,
    defaultSectionTitle: '',
    isVla: false,
    isGone: false,
    isFeaturedListingRetention: false,
    injectEasListing: false,
    isUsedBasic: false,
  };

  /**
   * Gets inventory.
   * @returns {Object[]}
   */
  getInventoryResults = () => {
    const { inventory } = this.props;
    return isArray(inventory) ? inventory : get(inventory, 'results', []);
  };

  /**
   * Gets section title and link to SRP
   * @param {Boolean} hasMoreInventory
   * @returns {Object}
   */
  getTitleSectionData = hasMoreInventory => {
    const { vinData, isNewDesign, isPartnerListing, defaultSectionTitle, isVla, isGone, isFeaturedListingRetention } =
      this.props;
    const make = get(vinData, 'vehicleInfo.styleInfo.make');
    const model = get(vinData, 'vehicleInfo.styleInfo.model', '');
    const year = get(vinData, 'vehicleInfo.styleInfo.year');
    const { type } = vinData;

    if (!isGone && (!isPartnerListing || isVla) && isNewDesign && !isFeaturedListingRetention) {
      return {
        srpLink: srpLinkBuilder({
          make,
          model,
          year,
          inventorytype: !isNew(type) && !isPartnerListing ? 'used,cpo' : undefined,
          sort: !isPartnerListing ? 'bestDealsConquest' : undefined,
        }),
        sectionTitle: `Similar ${make} ${getLastWordPlural(model)} Near You`,
      };
    }

    if (hasMoreInventory) {
      const rooftopId = get(vinData, 'dealerInfo.rooftopId');

      return {
        srpLink: srpLinkBuilder({ dealerlocationid: rooftopId }),
        sectionTitle: defaultSectionTitle || 'More inventory from this dealer',
      };
    }

    return {
      srpLink: srpLinkBuilder({ make, model, year }),
      sectionTitle: defaultSectionTitle || 'Similar Inventory from Edmunds Dealers',
    };
  };

  /**
   * Filters inventories based on whether the current VIN is in the results.
   * If VIN is in array, filter it out. If it's not, remove the last item, because API returns one extra item when VIN is not found to ensure we always have a consistent number of results.
   * @return {Object[]}
   */
  filterInventories = () => {
    const {
      vinData: { vin },
    } = this.props;
    const inventories = this.getInventoryResults();
    const vinIndex = inventories.findIndex(vehicle => vehicle.vin === vin);

    if (vinIndex !== -1) {
      return inventories.filter(vehicle => vehicle.vin !== vin);
    }

    return inventories.slice(0, -1);
  };

  render() {
    const { vinData, isMobile, isUsedBasic, injectEasListing } = this.props;
    const hasDealerMoreInventory = isPartnerDealer(vinData) && hasDealerAddressInfo(vinData);
    const { sectionTitle, srpLink } = this.getTitleSectionData(hasDealerMoreInventory);
    const vehicles = this.filterInventories();

    if (!vehicles.length && injectEasListing && isUsedBasic) {
      return <EasListingAd mobile={isMobile} similarVehicleTitle={sectionTitle} />;
    }

    if (!vehicles.length) return null;

    const { isNewDesign, visitorLocation, radius, paymentType, forceLease, isPartnerListing, isVla } = this.props;
    const listTrackingData = {
      creativeId: isNewDesign && isPartnerListing ? PARTNER_CREATIVE_ID : CREATIVE_ID,
      inventory: vehicles,
    };

    if ((!isPartnerListing || isVla) && isNewDesign && !isMobile) {
      return (
        <div className={classnames('bg-blue-90 overlay-white-40 pt-2 mb-2 mt-2 pb-0_5')}>
          <Container tag="div" className="vdp-content-wrapper">
            <VdpSimilarVehiclesContent
              listTrackingData={listTrackingData}
              sectionTitle={sectionTitle}
              srpLink={srpLink}
              vehicles={vehicles}
              hasMoreInventory={hasDealerMoreInventory}
              isMobile={isMobile}
              isNewDesign={isNewDesign}
              visitorLocation={visitorLocation}
              radius={radius}
              paymentType={paymentType}
              forceLease={forceLease}
              injectEasListing={injectEasListing}
              isUsedBasic={isUsedBasic}
            />
          </Container>
        </div>
      );
    }

    return (
      <VdpSimilarVehiclesContent
        listTrackingData={listTrackingData}
        sectionTitle={sectionTitle}
        srpLink={srpLink}
        vehicles={vehicles}
        hasMoreInventory={hasDealerMoreInventory}
        isMobile={isMobile}
        isNewDesign={isNewDesign}
        visitorLocation={visitorLocation}
        radius={radius}
        paymentType={paymentType}
        forceLease={forceLease}
        isPartnerListing={isPartnerListing && !isVla}
        injectEasListing={injectEasListing}
        isUsedBasic={isUsedBasic}
      />
    );
  }
}

export const stateToPropsConfig = {
  inventory: bindToPath('vinsV2', InventorySimilarVinsModel),
  visitorLocation: bindToPath('location', VisitorModel),
};

export const VdpSimilarVehicles = connectToModel(VdpSimilarVehiclesUI, stateToPropsConfig);
